import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  fetchMacbooks, createMacbook, updateMacbook, deleteMacbook, markSold,
  fetchSiteSettings, updateSiteSettings, fetchOrders, uploadImage, verifyAdmin
} from "@/lib/api";
import {
  Plus, Trash2, Edit, Tag, Laptop, ArrowLeft, Loader2, X, Upload,
  CheckCircle, Package, ShoppingCart, Settings, LayoutDashboard, LogOut
} from "lucide-react";
import AdminLoginPage from "@/pages/AdminLoginPage";

const conditionLabels = { excellent: "Excellent", very_good: "Very Good", good: "Good" };
const tierLabels = { basic: "Basic", warranty: "With Extended Warranty" };
const categoryLabels = { air: "MacBook Air", pro: "MacBook Pro" };

const emptyForm = {
  internal_title: "", notes: "", serial_number: "", condition_notes: "",
  photos: [], category: "air", tier: "basic", condition: "excellent",
  price: "", short_description: "", status: "available",
  seo_title: "", seo_description: "",
};

export default function AdminPage({ settings, onSettingsUpdate }) {
  const [authenticated, setAuthenticated] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("pennymacs_token");
    if (!token) {
      setAuthChecking(false);
      return;
    }
    verifyAdmin()
      .then(() => setAuthenticated(true))
      .catch(() => {
        localStorage.removeItem("pennymacs_token");
        localStorage.removeItem("pennymacs_email");
      })
      .finally(() => setAuthChecking(false));
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("pennymacs_token");
    localStorage.removeItem("pennymacs_email");
    setAuthenticated(false);
    toast.success("Logged out");
  };

  if (authChecking) {
    return (
      <div className="min-h-screen bg-[#f5f5f7] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#86868b]" />
      </div>
    );
  }

  if (!authenticated) {
    return <AdminLoginPage onLogin={() => setAuthenticated(true)} />;
  }

  return <AdminDashboard settings={settings} onSettingsUpdate={onSettingsUpdate} onLogout={handleLogout} />;
}

function AdminDashboard({ settings, onSettingsUpdate, onLogout }) {
  const [tab, setTab] = useState("inventory");
  const [macbooks, setMacbooks] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [siteForm, setSiteForm] = useState({
    business_name: "", phone: "", email: "", address: "", hours: "",
    deal_enabled: false, deal_macbook_id: "", deal_price: "", deal_label: "Deal of the Day",
  });
  const [photoUrl, setPhotoUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [mbRes, ordRes, setRes] = await Promise.all([
        fetchMacbooks(), fetchOrders(), fetchSiteSettings()
      ]);
      setMacbooks(mbRes.data);
      setOrders(ordRes.data);
      setSiteForm({
        business_name: setRes.data.business_name || "",
        phone: setRes.data.phone || "",
        email: setRes.data.email || "",
        address: setRes.data.address || "",
        hours: setRes.data.hours || "",
        deal_enabled: setRes.data.deal_enabled || false,
        deal_macbook_id: setRes.data.deal_macbook_id || "",
        deal_price: setRes.data.deal_price ? String(setRes.data.deal_price) : "",
        deal_label: setRes.data.deal_label || "Deal of the Day",
      });
    } catch (e) {
      toast.error("Failed to load data");
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async () => {
    if (!form.internal_title || !form.price) {
      toast.error("Title and price are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price) };
      if (editingId) {
        await updateMacbook(editingId, payload);
        toast.success("MacBook updated");
      } else {
        await createMacbook(payload);
        toast.success("MacBook added");
      }
      setForm({ ...emptyForm });
      setEditingId(null);
      setTab("inventory");
      loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const handleEdit = (mb) => {
    setForm({
      internal_title: mb.internal_title || "",
      notes: mb.notes || "",
      serial_number: mb.serial_number || "",
      condition_notes: mb.condition_notes || "",
      photos: mb.photos || [],
      category: mb.category || "air",
      tier: mb.tier || "basic",
      condition: mb.condition || "excellent",
      price: mb.price?.toString() || "",
      short_description: mb.short_description || "",
      status: mb.status || "available",
      seo_title: mb.seo_title || "",
      seo_description: mb.seo_description || "",
    });
    setEditingId(mb.id);
    setTab("add");
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    try {
      await deleteMacbook(deleteDialog);
      toast.success("MacBook deleted");
      setDeleteDialog(null);
      loadData();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const handleMarkSold = async (id) => {
    try {
      await markSold(id);
      toast.success("Marked as sold");
      loadData();
    } catch (e) {
      toast.error("Failed to mark as sold");
    }
  };

  const handleSiteSettings = async () => {
    try {
      const payload = {
        ...siteForm,
        deal_price: siteForm.deal_price ? parseFloat(siteForm.deal_price) : 0,
      };
      const res = await updateSiteSettings(payload);
      toast.success("Settings saved");
      onSettingsUpdate(res.data);
    } catch (e) {
      toast.error("Failed to save settings");
    }
  };

  const addPhotoUrl = () => {
    if (!photoUrl.trim()) return;
    setForm((p) => ({ ...p, photos: [...p.photos, photoUrl.trim()] }));
    setPhotoUrl("");
  };

  const removePhoto = (index) => {
    setForm((p) => ({ ...p, photos: p.photos.filter((_, i) => i !== index) }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadImage(file);
      setForm((p) => ({ ...p, photos: [...p.photos, res.data.url] }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error("Upload failed");
    }
    setUploading(false);
    e.target.value = "";
  };

  const availableCount = macbooks.filter((m) => m.status === "available").length;
  const soldCount = macbooks.filter((m) => m.status === "sold").length;

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#f5f5f7]">
      {/* Admin Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LayoutDashboard className="w-5 h-5 text-[#1d1d1f]" />
            <h1 className="text-lg font-bold text-[#1d1d1f]">Admin Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="admin-back-to-site"
              className="text-sm text-[#86868b] hover:text-[#1d1d1f] transition-colors flex items-center gap-1"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              View Site
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={onLogout}
              className="text-sm text-[#86868b] hover:text-red-600"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-3.5 h-3.5 mr-1" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Total", value: macbooks.length, icon: Laptop },
            { label: "Available", value: availableCount, icon: CheckCircle },
            { label: "Sold", value: soldCount, icon: Tag },
            { label: "Orders", value: orders.length, icon: ShoppingCart },
          ].map((s, i) => (
            <div
              key={i}
              className="bg-white p-5 rounded-xl border border-gray-100"
              data-testid={`stat-${s.label.toLowerCase()}`}
            >
              <s.icon className="w-5 h-5 text-[#86868b] mb-2" />
              <p className="text-2xl font-bold text-[#1d1d1f]">{s.value}</p>
              <p className="text-xs text-[#86868b] mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white border border-gray-200 rounded-xl p-1 mb-6" data-testid="admin-tabs">
            <TabsTrigger value="inventory" data-testid="tab-inventory" className="rounded-lg text-sm">
              <Package className="w-4 h-4 mr-1.5" />
              Inventory
            </TabsTrigger>
            <TabsTrigger value="add" data-testid="tab-add" className="rounded-lg text-sm">
              <Plus className="w-4 h-4 mr-1.5" />
              {editingId ? "Edit" : "Add New"}
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings" className="rounded-lg text-sm">
              <Settings className="w-4 h-4 mr-1.5" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-lg text-sm">
              <ShoppingCart className="w-4 h-4 mr-1.5" />
              Orders
            </TabsTrigger>
          </TabsList>

          {/* Inventory Tab */}
          <TabsContent value="inventory" data-testid="inventory-tab">
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-5 h-5 animate-spin text-[#86868b]" />
                </div>
              ) : macbooks.length === 0 ? (
                <div className="text-center py-16">
                  <Laptop className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-[#86868b]">No MacBooks yet</p>
                  <Button
                    className="mt-4 rounded-full text-sm"
                    onClick={() => { setForm({ ...emptyForm }); setEditingId(null); setTab("add"); }}
                    data-testid="add-first-btn"
                  >
                    Add your first MacBook
                  </Button>
                </div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {macbooks.map((mb) => (
                    <div
                      key={mb.id}
                      className="flex items-center gap-4 p-4 hover:bg-gray-50/50 transition-colors"
                      data-testid={`inventory-item-${mb.id}`}
                    >
                      <div className="w-14 h-14 rounded-xl bg-[#f5f5f7] overflow-hidden shrink-0 flex items-center justify-center">
                        {mb.photos?.length > 0 ? (
                          <img
                            src={mb.photos[0]}
                            alt="" className="w-full h-full object-cover"
                          />
                        ) : (
                          <Laptop className="w-6 h-6 text-gray-300" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[#1d1d1f] truncate">
                          {mb.internal_title}
                        </p>
                        <p className="text-xs text-[#86868b]">
                          {categoryLabels[mb.category]} · {tierLabels[mb.tier]} · {conditionLabels[mb.condition]}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {mb.status === "available" && (
                          <span className="text-sm font-semibold text-[#1d1d1f]">${mb.price?.toLocaleString()}</span>
                        )}
                        <Badge
                          className={`text-[10px] font-medium border ${
                            mb.status === "available"
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : "bg-red-50 text-red-700 border-red-200"
                          }`}
                        >
                          {mb.status === "available" ? "Available" : "Sold"}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {mb.status === "available" && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleMarkSold(mb.id)}
                            data-testid={`mark-sold-${mb.id}`}
                            title="Mark as sold"
                          >
                            <Tag className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleEdit(mb)}
                          data-testid={`edit-btn-${mb.id}`}
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setDeleteDialog(mb.id)}
                          data-testid={`delete-btn-${mb.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Add/Edit Tab */}
          <TabsContent value="add" data-testid="add-tab">
            <div className="bg-white rounded-xl border border-gray-100 p-6 sm:p-8">
              <h2 className="text-base font-semibold text-[#1d1d1f] mb-6">
                {editingId ? "Edit MacBook" : "Add New MacBook"}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Internal fields */}
                <div className="space-y-4 md:col-span-2">
                  <p className="text-xs font-semibold text-[#86868b] uppercase tracking-wider">Internal Fields</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1d1d1f] block mb-1.5">Internal Title *</label>
                  <Input
                    data-testid="form-internal-title"
                    placeholder="e.g. MacBook Air M2 2022 Space Gray"
                    value={form.internal_title}
                    onChange={(e) => setForm((p) => ({ ...p, internal_title: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1d1d1f] block mb-1.5">Serial Number</label>
                  <Input
                    data-testid="form-serial-number"
                    placeholder="C02..."
                    value={form.serial_number}
                    onChange={(e) => setForm((p) => ({ ...p, serial_number: e.target.value }))}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-[#1d1d1f] block mb-1.5">Notes/Specs (internal)</label>
                  <Textarea
                    data-testid="form-notes"
                    placeholder="Internal notes..."
                    value={form.notes}
                    onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                    rows={2}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-[#1d1d1f] block mb-1.5">Condition Notes (internal)</label>
                  <Textarea
                    data-testid="form-condition-notes"
                    placeholder="Any wear or cosmetic notes..."
                    value={form.condition_notes}
                    onChange={(e) => setForm((p) => ({ ...p, condition_notes: e.target.value }))}
                    rows={2}
                  />
                </div>

                <Separator className="md:col-span-2 my-2" />

                {/* Customer-facing */}
                <div className="space-y-4 md:col-span-2">
                  <p className="text-xs font-semibold text-[#86868b] uppercase tracking-wider">Customer-Facing</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1d1d1f] block mb-1.5">Category *</label>
                  <Select value={form.category} onValueChange={(v) => setForm((p) => ({ ...p, category: v }))}>
                    <SelectTrigger data-testid="form-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="air">MacBook Air</SelectItem>
                      <SelectItem value="pro">MacBook Pro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1d1d1f] block mb-1.5">Tier *</label>
                  <Select value={form.tier} onValueChange={(v) => setForm((p) => ({ ...p, tier: v }))}>
                    <SelectTrigger data-testid="form-tier">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="basic">Basic</SelectItem>
                      <SelectItem value="warranty">With Extended Warranty</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1d1d1f] block mb-1.5">Condition *</label>
                  <Select value={form.condition} onValueChange={(v) => setForm((p) => ({ ...p, condition: v }))}>
                    <SelectTrigger data-testid="form-condition">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="excellent">Excellent</SelectItem>
                      <SelectItem value="very_good">Very Good</SelectItem>
                      <SelectItem value="good">Good</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1d1d1f] block mb-1.5">Price (USD) *</label>
                  <Input
                    data-testid="form-price"
                    type="number"
                    placeholder="699"
                    value={form.price}
                    onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1d1d1f] block mb-1.5">Status</label>
                  <Select value={form.status} onValueChange={(v) => setForm((p) => ({ ...p, status: v }))}>
                    <SelectTrigger data-testid="form-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available">Available</SelectItem>
                      <SelectItem value="sold">Sold</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-[#1d1d1f] block mb-1.5">Short Description</label>
                  <Textarea
                    data-testid="form-description"
                    placeholder="Professionally tested. Ready to use."
                    value={form.short_description}
                    onChange={(e) => setForm((p) => ({ ...p, short_description: e.target.value }))}
                    rows={2}
                  />
                </div>

                {/* Photos */}
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-[#1d1d1f] block mb-1.5">Photos</label>
                  <div className="flex gap-2 mb-3">
                    <Input
                      data-testid="form-photo-url"
                      placeholder="Paste image URL..."
                      value={photoUrl}
                      onChange={(e) => setPhotoUrl(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addPhotoUrl())}
                    />
                    <Button type="button" variant="outline" onClick={addPhotoUrl} data-testid="add-photo-url-btn">
                      Add
                    </Button>
                    <label className="cursor-pointer">
                      <input type="file" accept="image/*" className="hidden" onChange={handleFileUpload} data-testid="upload-photo-input" />
                      <Button type="button" variant="outline" className="pointer-events-none" disabled={uploading}>
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      </Button>
                    </label>
                  </div>
                  {form.photos.length > 0 && (
                    <div className="flex gap-2 flex-wrap">
                      {form.photos.map((url, i) => (
                        <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200 group">
                          <img
                            src={url}
                            alt={`Photo ${i + 1}`}
                            className="w-full h-full object-cover"
                          />
                          <button
                            onClick={() => removePhoto(i)}
                            className="absolute top-0.5 right-0.5 w-5 h-5 bg-black/60 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`remove-photo-${i}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Separator className="md:col-span-2 my-2" />

                {/* SEO */}
                <div className="space-y-4 md:col-span-2">
                  <p className="text-xs font-semibold text-[#86868b] uppercase tracking-wider">SEO</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1d1d1f] block mb-1.5">Page Title</label>
                  <Input
                    data-testid="form-seo-title"
                    placeholder="SEO page title"
                    value={form.seo_title}
                    onChange={(e) => setForm((p) => ({ ...p, seo_title: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1d1d1f] block mb-1.5">Meta Description</label>
                  <Input
                    data-testid="form-seo-desc"
                    placeholder="SEO meta description"
                    value={form.seo_description}
                    onChange={(e) => setForm((p) => ({ ...p, seo_description: e.target.value }))}
                  />
                </div>

                {/* Actions */}
                <div className="md:col-span-2 flex items-center gap-3 pt-4">
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="rounded-full px-8 h-11 text-sm font-semibold bg-[#1d1d1f] hover:bg-[#1d1d1f]/90"
                    data-testid="save-macbook-btn"
                  >
                    {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    {editingId ? "Update MacBook" : "Add MacBook"}
                  </Button>
                  {editingId && (
                    <Button
                      variant="outline"
                      className="rounded-full"
                      onClick={() => { setForm({ ...emptyForm }); setEditingId(null); }}
                      data-testid="cancel-edit-btn"
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" data-testid="settings-tab">
            <div className="bg-white rounded-xl border border-gray-100 p-6 sm:p-8">
              <h2 className="text-base font-semibold text-[#1d1d1f] mb-6">Site Settings</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl">
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-[#1d1d1f] block mb-1.5">Business Name</label>
                  <Input
                    data-testid="settings-business-name"
                    value={siteForm.business_name}
                    onChange={(e) => setSiteForm((p) => ({ ...p, business_name: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1d1d1f] block mb-1.5">Phone Number</label>
                  <Input
                    data-testid="settings-phone"
                    value={siteForm.phone}
                    onChange={(e) => setSiteForm((p) => ({ ...p, phone: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1d1d1f] block mb-1.5">Email Address</label>
                  <Input
                    data-testid="settings-email"
                    value={siteForm.email}
                    onChange={(e) => setSiteForm((p) => ({ ...p, email: e.target.value }))}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-[#1d1d1f] block mb-1.5">Physical Address</label>
                  <Input
                    data-testid="settings-address"
                    value={siteForm.address}
                    onChange={(e) => setSiteForm((p) => ({ ...p, address: e.target.value }))}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-[#1d1d1f] block mb-1.5">Business Hours</label>
                  <Input
                    data-testid="settings-hours"
                    placeholder="Mon-Fri 9am-5pm"
                    value={siteForm.hours}
                    onChange={(e) => setSiteForm((p) => ({ ...p, hours: e.target.value }))}
                  />
                </div>
                <Separator className="md:col-span-2 my-4" />

                {/* Deal of the Day */}
                <div className="md:col-span-2">
                  <p className="text-xs font-semibold text-[#86868b] uppercase tracking-wider mb-4">Deal of the Day</p>
                </div>
                <div className="md:col-span-2 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setSiteForm((p) => ({ ...p, deal_enabled: !p.deal_enabled }))}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${siteForm.deal_enabled ? "bg-emerald-500" : "bg-gray-200"}`}
                    data-testid="deal-toggle"
                  >
                    <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform ${siteForm.deal_enabled ? "translate-x-5" : "translate-x-0"}`} />
                  </button>
                  <span className="text-sm font-medium text-[#1d1d1f]">
                    {siteForm.deal_enabled ? "Deal is LIVE on homepage" : "Deal is OFF"}
                  </span>
                </div>
                {siteForm.deal_enabled && (
                  <>
                    <div>
                      <label className="text-sm font-medium text-[#1d1d1f] block mb-1.5">Deal Label</label>
                      <Input
                        data-testid="deal-label"
                        placeholder="Deal of the Day"
                        value={siteForm.deal_label}
                        onChange={(e) => setSiteForm((p) => ({ ...p, deal_label: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#1d1d1f] block mb-1.5">Deal Price (USD)</label>
                      <Input
                        data-testid="deal-price"
                        type="number"
                        placeholder="399"
                        value={siteForm.deal_price}
                        onChange={(e) => setSiteForm((p) => ({ ...p, deal_price: e.target.value }))}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-sm font-medium text-[#1d1d1f] block mb-1.5">Select MacBook for Deal</label>
                      {macbooks.filter((m) => m.status === "available").length === 0 ? (
                        <p className="text-sm text-[#86868b]">No available MacBooks. Add one first.</p>
                      ) : (
                        <div className="space-y-2">
                          {macbooks.filter((m) => m.status === "available").map((mb) => (
                            <button
                              key={mb.id}
                              type="button"
                              onClick={() => setSiteForm((p) => ({ ...p, deal_macbook_id: mb.id }))}
                              className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-colors ${siteForm.deal_macbook_id === mb.id ? "border-emerald-400 bg-emerald-50" : "border-gray-200 hover:border-gray-300"}`}
                              data-testid={`deal-select-${mb.id}`}
                            >
                              <div className="w-10 h-10 rounded-lg bg-[#f5f5f7] overflow-hidden shrink-0 flex items-center justify-center">
                                {mb.photos?.length > 0 ? (
                                  <img src={mb.photos[0]} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <Laptop className="w-5 h-5 text-gray-300" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-[#1d1d1f] truncate">{mb.internal_title}</p>
                                <p className="text-xs text-[#86868b]">{categoryLabels[mb.category]} · ${mb.price?.toLocaleString()}</p>
                              </div>
                              {siteForm.deal_macbook_id === mb.id && (
                                <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}

                <div className="md:col-span-2 pt-2">
                  <Button
                    onClick={handleSiteSettings}
                    className="rounded-full px-8 h-11 text-sm font-semibold bg-[#1d1d1f] hover:bg-[#1d1d1f]/90"
                    data-testid="save-settings-btn"
                  >
                    Save Settings
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders" data-testid="orders-tab">
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              {orders.length === 0 ? (
                <div className="text-center py-16">
                  <ShoppingCart className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-[#86868b]">No orders yet</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {orders.map((order) => (
                    <div
                      key={order.id}
                      className="flex items-center gap-4 p-4"
                      data-testid={`order-item-${order.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[#1d1d1f]">
                          {order.customer_name}
                        </p>
                        <p className="text-xs text-[#86868b]">{order.customer_email}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-semibold text-[#1d1d1f]">${order.amount?.toLocaleString()}</p>
                        <p className="text-xs text-[#86868b]">
                          {categoryLabels[order.macbook_category]} · {tierLabels[order.macbook_tier]}
                        </p>
                      </div>
                      <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border text-[10px]">
                        {order.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Delete Dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete MacBook</DialogTitle>
            <DialogDescription>
              This will permanently remove this MacBook from inventory. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)} data-testid="cancel-delete-btn">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
